import {
  Box,
  Typography,
  TextField,
  IconButton,
  Autocomplete,
} from "@mui/material";
import { Check, X, Plus } from "@phosphor-icons/react";
import { MaterialItem } from "../materialItem/MaterialItem";
import { StyledMaterialTable } from "./MaterialTable.styled";
import { useState, useContext, useMemo, useEffect } from "react";
import { LanguageContext } from "../../globals/LanguageContext";
import { Material } from "../../models/Material";
import { useMaterial } from "../../stores/useMaterial";
import { useMessage } from "../messagePoppup/MessageContext";
import { useProfile } from "../../stores/useProfile";
import { useCrate } from "../../stores/useCrate";

type MaterialTableProps = {
  crateId?: number;
  filteredMaterials: Material[];
  onSave: () => void;
};

export const MaterialTable = ({
  crateId,
  filteredMaterials,
  onSave,
}: MaterialTableProps) => {
  const [createNewMaterial, setCreateNewMaterial] = useState<boolean>(false);
  const [editedValues, setEditedValues] = useState<Partial<Material>>({});
  const [availableMaterials, setAvailableMaterials] = useState<Material[]>([]);
  const { user } = useProfile();
  const { dictionary } = useContext(LanguageContext);
  const { addMaterial, fetchMaterials } = useMaterial();
  const {addMaterialToCrate} = useCrate();
  const { showMessage } = useMessage();


  const getAllMaterials = async () => {
    if (!user) return;
    const res = await fetchMaterials(user);
    if (res) setAvailableMaterials(res);
  };

  useEffect(() => {
    getAllMaterials();
  }, [user]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { id, value } = e.target;
    setEditedValues((prev) => ({ ...prev, [id]: value }));
  };

  const handleSave = async () => {
    if (!user) return;
    const response = crateId ? await addMaterialToCrate(user, crateId, editedValues.id!) : await addMaterial(user, editedValues as Material);
    if (response === 201) setCreateNewMaterial(false);
    if (response) showMessage({ statusCode: response });
    onSave();
  };

  return (
    <StyledMaterialTable>
      <Box className="header">
        <Typography variant="h4" className="id">
          {dictionary.pages.materials.table.id}
        </Typography>
        <Typography variant="h4" className="brand">
          {dictionary.pages.materials.table.brand}
        </Typography>
        <Typography variant="h4" className="material">
          {dictionary.pages.materials.table.material}
        </Typography>
        {!crateId && (
          <>
            <Typography variant="h4" className="netprice">
              {dictionary.pages.materials.table.netprice}
            </Typography>
            <Typography variant="h4" className="grossprice">
              {dictionary.pages.materials.table.grossprice}
            </Typography>
          </>
        )}

        <Typography variant="h4" className="stock">
          {dictionary.pages.materials.table.stock}
        </Typography>
        <Typography variant="h4" className="actions">
          {dictionary.pages.materials.table.actions}
        </Typography>
      </Box>

      <Box className="content">
        {createNewMaterial ? (
          <Box className="row">
            <Typography variant="h4" className="id" />

            {crateId ? (
              <Autocomplete
                  options={availableMaterials}
                  getOptionLabel={(option) =>
                    `${option.brand} - ${option.name}`
                  }
                  onChange={(_, newValue) => {
                    if (newValue) {
                      setEditedValues(newValue);
                    }
                  }}
                  isOptionEqualToValue={(option, value) =>
                    option.id === value.id
                  }
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Select Material"
                      variant="outlined"
                      fullWidth
                    />
                  )}
                />
            ) : (
              <>
                <Box className="brand">
                  <TextField
                    id="brand"
                    label="Brand"
                    fullWidth
                    variant="outlined"
                    onChange={handleChange}
                    InputProps={{ disableUnderline: true }}
                  />
                </Box>
                <Box className="material">
                  <TextField
                    id="name"
                    label="Material"
                    fullWidth
                    variant="outlined"
                    onChange={handleChange}
                    InputProps={{ disableUnderline: true }}
                  />
                </Box>
                <Box className="netprice">
                  <TextField
                    id="netPrice"
                    label="Net (€)"
                    fullWidth
                    variant="outlined"
                    size="small"
                    onChange={handleChange}
                    InputProps={{ disableUnderline: true }}
                  />
                </Box>
                <Box className="grossprice">
                  <TextField
                    id="grossPrice"
                    label="Gross (€)"
                    variant="outlined"
                    fullWidth
                    size="small"
                    onChange={handleChange}
                    InputProps={{ disableUnderline: true }}
                  />
                </Box>
                <Box className="stock">
                  <TextField
                    id="stock"
                    label="Stock"
                    variant="outlined"
                    fullWidth
                    size="small"
                    onChange={handleChange}
                    InputProps={{ disableUnderline: true }}
                  />
                </Box>
              </>
            )}

            <Box className="actions">
              <IconButton onClick={handleSave}>
                <Check size={24} weight="bold" />
              </IconButton>
              <IconButton onClick={() => setCreateNewMaterial(false)}>
                <X size={24} weight="bold" />
              </IconButton>
            </Box>
          </Box>
        ) : (
          <Box className="row addBtn">
            <IconButton onClick={() => setCreateNewMaterial(true)}>
              <Plus size={24} />
            </IconButton>
          </Box>
        )}

        {filteredMaterials.length === 0 ? (
          <Typography variant="h4">
            {dictionary.pages.materials.nomaterials}
          </Typography>
        ) : (
          filteredMaterials.map((material) => (
            <MaterialItem
              crateId={crateId}
              key={material.id}
              material={material}
              onSave={onSave}
            />
          ))
        )}
      </Box>
    </StyledMaterialTable>
  );
};
